import React, { useEffect } from 'react';
import { Form, Skeleton } from 'antd';
import { customReportIconList } from './Icons';
import { useDataExtractionReport } from '@/context/DataExtractionReport';
import { useGetTotalCreatedCustomReports } from '@/hooks/useGetTotalCreatedCustomReports';
import { RadioGroupCustomReport, type RadioGroupCustomReportOption } from './components/RadioGroup';
import type { CustomReport, DataExtractionData } from '@/components/Settings/DataExtraction/types';
import * as S from '@/components/Settings/DataExtraction/styles';

const { useWatch } = Form;

type CustomReportPatch = Pick<DataExtractionData, 'custom_report' | 'custom_report_id'>;

export function ReportsAlreadyCreatedForm() {
	const { dataExtractionReport, onSetDataExtractionReport } = useDataExtractionReport();

	const selectedReportTypeId = useWatch(['reports', 'custom_report_type_id']);

	const { isLoading, data = [] } = useGetTotalCreatedCustomReports({
		organization_id: dataExtractionReport?.organization_id!,
		company_id: dataExtractionReport?.company_id!,
		sector_id: dataExtractionReport?.sector_id!,
		line_id: dataExtractionReport?.line_id!,
		workstation_id: dataExtractionReport?.workstation_id!,
		start_date: dataExtractionReport?.start_date!,
		end_date: dataExtractionReport?.end_date!
	});

	const options: RadioGroupCustomReportOption[] = customReportIconList.map((value) => {
		const customReport = data.find((report: CustomReport) => report.name === value.name);

		return {
			icon: value.icon,
			label: value.label,
			disabled: !customReport || customReport.total_reports <= 0,
			value: customReport?.id! || '',
			total: customReport?.total_reports ?? 0,
			acronym: value.acronym
		};
	});

	function buildReportUpdateFields(selectedId: string | null, reports: CustomReport[]): CustomReportPatch {
		if (!selectedId) {
			return { custom_report: undefined, custom_report_id: null };
		}

		const targetReport = reports.find((report) => report.id === selectedId);

		if (!targetReport) {
			return { custom_report: undefined, custom_report_id: selectedId };
		}

		return {
			custom_report_id: selectedId,
			custom_report: {
				acronym: targetReport.acronym,
				name: targetReport.formatted_name!,
				report_type: targetReport.report_type
			}
		};
	}

	useEffect(() => {
		if (!dataExtractionReport) return;
		if (dataExtractionReport.custom_report_id === selectedReportTypeId) return;

		const reportUpdateData = buildReportUpdateFields(selectedReportTypeId, data);

		onSetDataExtractionReport({
			...dataExtractionReport,
			...reportUpdateData
		});
	}, [selectedReportTypeId, data, dataExtractionReport, onSetDataExtractionReport]);

	if (isLoading) {
		return <Skeleton.Input active />;
	}

	return (
		<S.FormItemWrapper name={['reports', 'custom_report_type_id']}>
			<RadioGroupCustomReport options={options} customReports={data} />
		</S.FormItemWrapper>
	);
}
